/**
 * Account profile — a per-account view off the chart of accounts. Shows the
 * account's identity (code/name/type/parent/currency/status), headline balance +
 * debit/credit totals, its sub-accounts, and a full ledger of the journal lines
 * that hit it, with a correct running balance and a client-side date window.
 *
 * Every figure is kept per TRANSACTION currency (GET
 * /general-ledger/accounts/:id/ledger): a cash box or bank that holds dollars
 * and dinars shows how many of EACH are in it, with the base equivalent beside
 * it — not only one base figure that hides the dollars and moves with the
 * rate. Balances and running balances are computed on the server over the
 * account's whole history, so a busy account is right however long it is.
 */

import type { Account, AccountCurrencyLedger, AccountType } from '@erp/shared';
import { ArrowLeft, Ban, BookOpen, FolderTree, Hash, RotateCcw, Scale } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Card, Input, Skeleton } from '../../components/shadcn/index.js';
import { Button, confirmDialog, EmptyState, StatCard } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { formatDate, formatNumber } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';

/** Account type → a shell-bridge CSS var, for the type chip (matches the CoA list). */
const TYPE_VAR: Record<AccountType, string> = {
  asset: '--viz-1',
  liability: '--danger',
  equity: '--accent',
  revenue: '--success',
  expense: '--info',
};


export function AccountProfilePage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation('accounts');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('accounts.write');
  const baseCurrency = useAuthStore((s) => s.tenant?.baseCurrencyCode) ?? 'USD';

  const [account, setAccount] = useState<Account | null>(null);
  const [all, setAll] = useState<Account[]>([]);
  const [ledger, setLedger] = useState<AccountCurrencyLedger | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  // '' = every currency; else one transaction currency.
  const [ccy, setCcy] = useState('');

  const loadLedger = async (): Promise<void> => {
    const q = new URLSearchParams({ limit: '1000', ...(from ? { from } : {}), ...(to ? { to } : {}) });
    setLedger((await api().get<AccountCurrencyLedger>(`/general-ledger/accounts/${id}/ledger?${q.toString()}`)).data);
  };
  const reload = async (): Promise<void> => {
    const [acc, list] = await Promise.all([
      api().get<Account>(`/accounts/${id}`),
      api().get<{ items: Account[] }>('/accounts'),
    ]);
    setAccount(acc.data);
    setAll(list.data.items);
    await loadLedger();
  };
  useEffect(() => {
    setAccount(null);
    setLedger(null);
    setCcy('');
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => {
    if (account) void loadLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  const locale = i18n.language;

  const children = useMemo(
    () => all.filter((a) => a.parentId === id).sort((a, b) => a.code.localeCompare(b.code)),
    [all, id],
  );
  const parent = useMemo(
    () => (account?.parentId ? all.find((a) => a.id === account.parentId) ?? null : null),
    [all, account],
  );

  const isDebitNormal = ledger?.debitNormal ?? (account ? account.accountType === 'asset' || account.accountType === 'expense' : true);
  const currencies = ledger?.currencies ?? [];
  const multi = currencies.length > 1;
  const lines = useMemo(
    () => (ledger?.lines ?? []).filter((l) => !ccy || l.currencyCode === ccy),
    [ledger, ccy],
  );
  const truncated = (ledger?.total ?? 0) > (ledger?.lines.length ?? 0);
  const money = (amount: string | number, currency: string): string =>
    formatMoney(typeof amount === 'number' ? amount.toFixed(4) : amount, { locale, currency });
  const nonZero = (v: string): boolean => Math.abs(Number(v)) > 0.00005;

  const onDeactivate = async (): Promise<void> => {
    if (!account) return;
    if (!(await confirmDialog({ message: t('actions.confirmDeactivate', { code: account.code, name: account.name }), tone: 'danger' }))) return;
    try {
      await api().delete(`/accounts/${account.id}`);
      await reload();
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      toast.error(code === 'conflict' ? t('errors.hasActiveChildren') : tc('errors:server'));
    }
  };
  const onReactivate = async (): Promise<void> => {
    if (!account) return;
    if (!(await confirmDialog({ message: t('actions.confirmReactivate', { code: account.code, name: account.name }) }))) return;
    try {
      await api().patch(`/accounts/${account.id}`, { isActive: true });
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  if (!account || ledger === null) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-28 w-full" />
        <Skeleton className="mt-4 h-64 w-full" />
      </Card>
    );
  }

  const typeChip = (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
      style={{
        background: `color-mix(in oklab, var(${TYPE_VAR[account.accountType]}) 16%, transparent)`,
        color: `var(${TYPE_VAR[account.accountType]})`,
      }}
    >
      {t(`type.${account.accountType}`)}
    </span>
  );

  return (
    <div className="flex flex-col gap-4">
      <Link to="/accounting/accounts" className="inline-flex w-fit items-center gap-1.5 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" aria-hidden />
        {t('profile.back', { defaultValue: 'Chart of accounts' })}
      </Link>

      {/* identity */}
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="font-mono text-lg font-bold text-brand">{account.code}</span>
              <h1 className="text-2xl font-semibold">{account.name}</h1>
              {typeChip}
              <Badge variant={account.isActive ? 'success' : 'secondary'}>
                {t(`status.${account.isActive ? 'active' : 'inactive'}`)}
              </Badge>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-fg-muted">
              <span>{t('list.columns.currency')}: <span className="font-medium text-fg">{account.currencyCode}</span></span>
              {account.accountSubtype && (
                <span>{t('create.subtype')}: <span className="font-medium text-fg">{account.accountSubtype}</span></span>
              )}
              <span>
                {t('create.parent')}:{' '}
                {parent ? (
                  <Link to={`/accounting/accounts/${parent.id}`} className="font-medium text-brand hover:underline">
                    {parent.code} — {parent.name}
                  </Link>
                ) : (
                  <span className="font-medium text-fg">{t('create.parentNone')}</span>
                )}
              </span>
            </div>
            {account.description && <p className="mt-2 max-w-2xl text-sm text-fg-muted">{account.description}</p>}
          </div>
          {canWrite && (
            <div className="flex items-center gap-2">
              {account.isActive ? (
                <Button variant="secondary" onClick={() => void onDeactivate()}>
                  <Ban className="me-1.5 h-4 w-4" aria-hidden />{t('actions.deactivate')}
                </Button>
              ) : (
                <Button variant="secondary" onClick={() => void onReactivate()}>
                  <RotateCcw className="me-1.5 h-4 w-4" aria-hidden />{t('actions.reactivate')}
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Balance per currency — what is actually in the account in each —
          then the base-currency total they add up to. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {currencies.map((c) => (
          <StatCard
            key={c.currencyCode}
            label={`${t('profile.balance', { defaultValue: 'Balance' })} · ${c.currencyCode}`}
            value={money(c.closing, c.currencyCode)}
            icon={Scale}
            accent={`var(${TYPE_VAR[account.accountType]})`}
            hint={c.currencyCode === baseCurrency
              ? `${t('profile.debits', { defaultValue: 'Debits' })} ${money(c.debit, c.currencyCode)} · ${t('profile.credits', { defaultValue: 'Credits' })} ${money(c.credit, c.currencyCode)}`
              : `≈ ${money(c.baseClosing, baseCurrency)}`}
          />
        ))}
        {(multi || currencies.length === 0) && (
          <StatCard
            label={t('profile.baseTotal', { ccy: baseCurrency })}
            value={money(ledger.baseClosing, baseCurrency)}
            icon={Scale}
            hint={t(`profile.normal.${isDebitNormal ? 'debit' : 'credit'}`, { defaultValue: isDebitNormal ? 'Debit-normal' : 'Credit-normal' })}
          />
        )}
        <StatCard label={t('profile.transactions', { defaultValue: 'Transactions' })} value={formatNumber(ledger.total, { locale })} icon={Hash} />
      </div>

      {/* sub-accounts */}
      {children.length > 0 && (
        <Card className="p-5">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <FolderTree className="h-4 w-4 text-fg-muted" aria-hidden />
            {t('profile.subAccounts', { defaultValue: 'Sub-accounts' })}
            <span className="text-fg-muted">({children.length})</span>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {children.map((c) => (
              <Link
                key={c.id}
                to={`/accounting/accounts/${c.id}`}
                className="flex items-center gap-2 rounded-lg border border-border bg-bg-subtle px-3 py-2 text-sm hover:border-brand"
              >
                <span className="font-mono text-xs font-semibold text-brand">{c.code}</span>
                <span className="truncate text-fg">{c.name}</span>
                {!c.isActive && <span className="ms-auto text-xs text-fg-muted">{t('status.inactive')}</span>}
              </Link>
            ))}
          </div>
        </Card>
      )}

      {/* ledger */}
      <Card className="overflow-hidden p-0">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <BookOpen className="h-4 w-4 text-fg-muted" aria-hidden />
            {t('profile.ledger', { defaultValue: 'Ledger' })}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {multi && (
              <div className="inline-flex gap-1 rounded-lg border border-border p-0.5" role="tablist">
                {['', ...currencies.map((c) => c.currencyCode)].map((c) => (
                  <button
                    key={c || 'all'}
                    type="button"
                    role="tab"
                    aria-selected={ccy === c}
                    onClick={() => setCcy(c)}
                    className={`rounded-md px-2.5 py-1 text-xs font-semibold ${ccy === c ? 'bg-brand text-brand-contrast' : 'text-fg-muted hover:bg-bg-subtle'}`}
                  >
                    {c || t('profile.allCurrencies')}
                  </button>
                ))}
              </div>
            )}
            <label className="flex items-center gap-1.5 text-xs text-fg-muted">
              {t('profile.from', { defaultValue: 'From' })}
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-auto" />
            </label>
            <label className="flex items-center gap-1.5 text-xs text-fg-muted">
              {t('profile.to', { defaultValue: 'To' })}
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-auto" />
            </label>
            {(from || to) && (
              <Button variant="ghost" size="sm" onClick={() => { setFrom(''); setTo(''); }}>
                {tc('actions.clear', { defaultValue: 'Clear' })}
              </Button>
            )}
          </div>
        </div>

        {lines.length === 0 ? (
          <EmptyState
            icon={<BookOpen className="h-8 w-8" aria-hidden />}
            title={t('profile.noActivity', { defaultValue: 'No postings yet' })}
            description={t('profile.noActivityDescription', { defaultValue: 'Journal lines that post against this account will appear here.' })}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-fg-muted">
                  <th className="px-4 py-2.5 text-start font-semibold">{t('profile.col.date', { defaultValue: 'Date' })}</th>
                  <th className="px-4 py-2.5 text-start font-semibold">{t('profile.col.entry', { defaultValue: 'Entry #' })}</th>
                  <th className="px-4 py-2.5 text-start font-semibold">{t('profile.col.description', { defaultValue: 'Description' })}</th>
                  <th className="px-4 py-2.5 text-end font-semibold">{t('profile.col.debit', { defaultValue: 'Debit' })}</th>
                  <th className="px-4 py-2.5 text-end font-semibold">{t('profile.col.credit', { defaultValue: 'Credit' })}</th>
                  <th className="px-4 py-2.5 text-end font-semibold">{t('profile.col.balance', { defaultValue: 'Balance' })}</th>
                  {multi && !ccy && (
                    <th className="px-4 py-2.5 text-end font-semibold">{t('profile.col.baseBalance', { ccy: baseCurrency })}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={`${l.entryId}-${l.lineNo}`} className="border-b border-border/60 last:border-0 hover:bg-bg-subtle">
                    <td className="whitespace-nowrap px-4 py-2.5 text-fg-muted">{formatDate(l.entryDate, { locale, timeZone: 'UTC' })}</td>
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <Link to={`/accounting/journals/${l.entryId}/print`} className="font-mono text-xs text-brand hover:underline">
                        {l.entryNumber}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-fg">
                      {l.description ?? <span className="text-fg-muted">—</span>}
                      {l.currencyCode !== baseCurrency && (
                        <span className="ms-2 whitespace-nowrap text-xs text-fg-muted" dir="ltr">
                          @ {Number(l.exchangeRate).toLocaleString('en', { maximumFractionDigits: 6 })} · ≈ {money(nonZero(l.baseDebit) ? l.baseDebit : l.baseCredit, baseCurrency)}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-end font-medium tabular-nums" dir="ltr">
                      {nonZero(l.debit) ? money(l.debit, l.currencyCode) : <span className="text-fg-subtle">—</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-end font-medium tabular-nums" dir="ltr">
                      {nonZero(l.credit) ? money(l.credit, l.currencyCode) : <span className="text-fg-subtle">—</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-end font-semibold tabular-nums" dir="ltr">{money(l.balance, l.currencyCode)}</td>
                    {multi && !ccy && (
                      <td className="whitespace-nowrap px-4 py-2.5 text-end tabular-nums text-fg-muted" dir="ltr">{money(l.baseBalance, baseCurrency)}</td>
                    )}
                  </tr>
                ))}
                {ledger.from && (
                  <tr className="bg-bg-subtle text-xs text-fg-muted">
                    <td className="px-4 py-2" colSpan={3}>{t('profile.broughtForward', { date: formatDate(ledger.from, { locale, timeZone: 'UTC' }) })}</td>
                    <td className="px-4 py-2" colSpan={2} />
                    <td className="whitespace-nowrap px-4 py-2 text-end font-semibold" dir="ltr">
                      {currencies.filter((c) => !ccy || c.currencyCode === ccy).map((c) => money(c.opening, c.currencyCode)).join(' · ')}
                    </td>
                    {multi && !ccy && <td className="whitespace-nowrap px-4 py-2 text-end" dir="ltr">{money(ledger.baseOpening, baseCurrency)}</td>}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {truncated && (
          <p className="border-t border-border px-4 py-2 text-xs text-fg-muted">
            {t('profile.latest', { n: ledger.lines.length, total: ledger.total })}
          </p>
        )}
      </Card>
    </div>
  );
}
